/** Training Request detail / approval page. */
import { Fragment, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Award,
  ArrowLeft,
  Briefcase,
  Building2,
  Calendar,
  CheckCircle2,
  Clock,
  Download,
  FileText,
  MapPin,
  MessageSquare,
  Tag,
  Trash2,
  Upload,
  User,
  UserCheck,
  Wallet,
  XCircle,
} from "lucide-react";
import { PageLoader } from "#/components/common/PageLoader";
import { ErrorState } from "#/components/common/ErrorState";
import { useConfirm } from "#/components/common/ConfirmProvider";
import { useToast } from "#/components/common/ToastProvider";
import { getErrorMessage } from "#/features/leave/hooks";
import { documentApi } from "#/features/documents/api";
import {
  useApproveTrainingRequest,
  useCancelTrainingRequest,
  useDeleteTrainingDocument,
  useRejectTrainingRequest,
  useSubmitTrainingRequest,
  useTrainingRequest,
  useUploadTrainingCertificate,
} from "../hooks";
import { TrainingEnrollmentForm } from "../components/TrainingEnrollmentForm";
import { formatUsername } from "../utils";
import type { TrainingRequestDocument, TrainingRequestStatus } from "../types";

function triggerBlobDownload(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

const STATUS_META: Record<TrainingRequestStatus, { label: string; cls: string; dot: string }> = {
  DRAFT:     { label: "Draft",     cls: "bg-slate-100 text-slate-700 ring-slate-200",      dot: "bg-slate-400" },
  SUBMITTED: { label: "Submitted", cls: "bg-amber-100 text-amber-800 ring-amber-200",      dot: "bg-amber-500" },
  APPROVED:  { label: "Approved",  cls: "bg-emerald-100 text-emerald-800 ring-emerald-200", dot: "bg-emerald-500" },
  REJECTED:  { label: "Rejected",  cls: "bg-red-100 text-red-700 ring-red-200",            dot: "bg-red-500" },
  CANCELLED: { label: "Cancelled", cls: "bg-slate-200 text-slate-600 ring-slate-300",      dot: "bg-slate-400" },
  COMPLETED: { label: "Completed", cls: "bg-indigo-100 text-indigo-800 ring-indigo-200",    dot: "bg-indigo-500" },
};

const FLOW: TrainingRequestStatus[] = ["DRAFT", "SUBMITTED", "APPROVED", "COMPLETED"];

function fmt(v?: string | null) {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }); }
  catch { return v; }
}

function fmtDateTime(v?: string | null) {
  if (!v) return "—";
  try { return new Date(v).toLocaleString(undefined, { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return v; }
}

function Field({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value?: string | number | null;
}) {
  if (value == null || value === "") return null;
  return (
    <div className="flex items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50/60 px-4 py-3">
      <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white text-slate-400 shadow-sm">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">{label}</p>
        <p className="mt-0.5 break-words text-sm font-semibold text-slate-800">{value}</p>
      </div>
    </div>
  );
}

function StatusStepper({ status }: { status: TrainingRequestStatus }) {
  if (status === "REJECTED" || status === "CANCELLED") {
    const meta = STATUS_META[status];
    return (
      <div className={`inline-flex items-center gap-2 rounded-2xl px-4 py-2.5 text-sm font-bold ring-1 ${meta.cls}`}>
        <XCircle className="h-4 w-4" />
        {meta.label}
      </div>
    );
  }

  const currentIdx = Math.max(FLOW.indexOf(status), 0);

  return (
    <div className="flex items-center">
      {FLOW.map((step, i) => {
        const done = i < currentIdx;
        const active = i === currentIdx;
        return (
          <Fragment key={step}>
            <div className="flex flex-col items-center gap-1.5">
              <div
                className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold ring-2 ${
                  done
                    ? "bg-emerald-600 text-white ring-emerald-600"
                    : active
                    ? "bg-white text-emerald-700 ring-emerald-600"
                    : "bg-white text-slate-300 ring-slate-200"
                }`}
              >
                {done ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
              </div>
              <span
                className={`text-[11px] font-semibold ${
                  active ? "text-emerald-700" : done ? "text-slate-600" : "text-slate-300"
                }`}
              >
                {STATUS_META[step].label}
              </span>
            </div>
            {i < FLOW.length - 1 && (
              <div className={`mx-1 h-0.5 flex-1 ${i < currentIdx ? "bg-emerald-600" : "bg-slate-200"}`} />
            )}
          </Fragment>
        );
      })}
    </div>
  );
}

type ActionDialog = "approve" | "reject" | null;

export default function TrainingCourseViewPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [dialog, setDialog] = useState<ActionDialog>(null);
  const confirm = useConfirm();
  const toast = useToast();

  const query   = useTrainingRequest(Number(id));
  const approve = useApproveTrainingRequest();
  const reject  = useRejectTrainingRequest();
  const submit  = useSubmitTrainingRequest();
  const cancel  = useCancelTrainingRequest();
  const uploadCertificate = useUploadTrainingCertificate();
  const deleteDocument    = useDeleteTrainingDocument();
  const certFileRef = useRef<HTMLInputElement>(null);
  const [downloadingId, setDownloadingId] = useState<number | null>(null);

  if (query.isLoading) return <PageLoader label="Loading training request…" />;
  if (query.isError || !query.data) {
    return <ErrorState title="Not found" message="This training request could not be loaded." />;
  }

  const r = query.data;
  const busy = approve.isPending || reject.isPending || submit.isPending || cancel.isPending;
  const meta = STATUS_META[r.status] ?? STATUS_META.DRAFT;
  const canUploadCertificate = r.status === "APPROVED" || r.status === "COMPLETED";
  // Once any approver has signed off, the requester can no longer self-cancel — only
  // the current approver (reject) or an admin can stop it from here. Mirrors the backend
  // check in TrainingRequestValidationService.ensureCanCancel.
  const alreadyApprovedAStep = (r.actionLogs ?? []).some((log) => log.action === "APPROVED");
  const canCancel = r.status === "DRAFT" || (r.status === "SUBMITTED" && !alreadyApprovedAStep);

  async function handleCertificateChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    try {
      await uploadCertificate.mutateAsync({ id: r.id, file });
      toast.success("Certificate uploaded.");
      query.refetch();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  async function handleDownloadDocument(doc: TrainingRequestDocument) {
    setDownloadingId(doc.id);
    try {
      const { blob, filename } = await documentApi.downloadBlob(doc.id);
      triggerBlobDownload(blob, filename ?? doc.originalFileName);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setDownloadingId(null);
    }
  }

  async function handleDeleteDocument(doc: TrainingRequestDocument) {
    if (!(await confirm(`Delete "${doc.originalFileName}"?`))) return;
    try {
      await deleteDocument.mutateAsync({ id: r.id, documentId: doc.id });
      toast.success("Document deleted.");
      query.refetch();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  async function handleApprove(comments?: string) {
    try {
      await approve.mutateAsync({ id: r.id, payload: { comments } });
      setDialog(null);
      query.refetch();
      toast.success("Training request approved.");
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  async function handleReject(comments?: string) {
    try {
      await reject.mutateAsync({ id: r.id, payload: { comments } });
      setDialog(null);
      query.refetch();
      toast.success("Training request rejected.");
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  async function handleSubmit() {
    try {
      await submit.mutateAsync(r.id);
      query.refetch();
      toast.success("Submitted for approval.");
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  async function handleCancel() {
    if (!(await confirm("Cancel this training request?"))) return;
    try {
      await cancel.mutateAsync({ id: r.id });
      toast.success("Training request cancelled.");
      navigate(-1);
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  }

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-6">
      <button
        onClick={() => navigate(-1)}
        className="inline-flex items-center gap-1.5 text-sm font-semibold text-slate-500 hover:text-slate-800"
      >
        <ArrowLeft className="h-4 w-4" />
        Back
      </button>

      {/* Header */}
      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-2xl font-black text-slate-900">{r.trainingTitle}</h1>
            {r.trainingRequestNo && (
              <p className="mt-1 text-xs font-semibold text-slate-400">{r.trainingRequestNo}</p>
            )}
          </div>
          <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ring-1 ${meta.cls}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`} />
            {meta.label}
          </span>
        </div>

        <div className="mt-5 overflow-x-auto">
          <StatusStepper status={r.status} />
        </div>
      </div>

      {/* Details */}
      <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-xs font-black uppercase tracking-wide text-slate-400">Details</h2>
        <div className="grid gap-3 sm:grid-cols-3">
          <Field icon={User}      label="Requested by"   value={r.employeeName} />
          {r.status === "SUBMITTED" && (
            <Field
              icon={UserCheck}
              label={r.currentStepName ? `Pending — ${r.currentStepName}` : "Pending approval"}
              value={r.currentApproverUsername ? formatUsername(r.currentApproverUsername) : "Unassigned"}
            />
          )}
          <Field icon={Building2} label="Department"     value={r.departmentName} />
          <Field icon={Tag}       label="Category"       value={r.categoryName} />
          <Field icon={Briefcase} label="Provider"       value={r.providerName} />
          <Field icon={MapPin}    label="Venue"          value={r.venue} />
          <Field
            icon={Wallet}
            label="Estimated cost"
            value={
              r.applicantEstimatedCost != null
                ? `KES ${r.applicantEstimatedCost.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                : null
            }
          />
          <Field icon={Calendar} label="Start date" value={fmt(r.trainingStartDate)} />
          <Field icon={Calendar} label="End date"   value={fmt(r.trainingEndDate)} />
          <Field icon={Clock}    label="Submitted"  value={fmtDateTime(r.submittedAt)} />
          {r.approvedAt  && <Field icon={CheckCircle2} label="Approved"  value={fmtDateTime(r.approvedAt)} />}
          {r.rejectedAt  && <Field icon={XCircle}      label="Rejected"  value={fmtDateTime(r.rejectedAt)} />}
          {r.completedAt && <Field icon={CheckCircle2} label="Completed" value={fmtDateTime(r.completedAt)} />}
        </div>

        {r.expectedOutcome && (
          <div className="mt-4 rounded-2xl border border-slate-100 bg-slate-50/60 px-4 py-3">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Expected Outcome</p>
            <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{r.expectedOutcome}</p>
          </div>
        )}
      </div>

      {/* Documents */}
      {(r.documents && r.documents.length > 0) || canUploadCertificate ? (
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-xs font-black uppercase tracking-wide text-slate-400">Documents</h2>
            {canUploadCertificate && (
              <>
                <input
                  ref={certFileRef}
                  type="file"
                  className="hidden"
                  onChange={handleCertificateChange}
                />
                <button
                  onClick={() => certFileRef.current?.click()}
                  disabled={uploadCertificate.isPending}
                  className="inline-flex items-center gap-1.5 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs font-bold text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                >
                  <Upload className="h-3.5 w-3.5" />
                  {uploadCertificate.isPending ? "Uploading…" : "Upload Certificate"}
                </button>
              </>
            )}
          </div>

          {r.documents && r.documents.length > 0 ? (
            <div className="space-y-2">
              {r.documents.map((d) => {
                const isCertificate = d.documentType === "CERTIFICATE";
                return (
                  <div key={d.id} className="flex items-center gap-3 rounded-2xl border border-slate-100 bg-slate-50/60 px-4 py-3">
                    <div className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl shadow-sm ${
                      isCertificate ? "bg-emerald-100 text-emerald-700" : "bg-white text-slate-400"
                    }`}>
                      {isCertificate ? <Award className="h-4 w-4" /> : <FileText className="h-4 w-4" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-slate-800">{d.originalFileName}</p>
                      <p className="text-xs text-slate-400">
                        {isCertificate ? "Certificate" : d.documentType ?? "Document"}
                        {d.createdAt ? ` · ${fmtDateTime(d.createdAt)}` : ""}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDownloadDocument(d)}
                      disabled={downloadingId === d.id}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-emerald-600 disabled:opacity-50"
                      title="Download"
                    >
                      <Download className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDeleteDocument(d)}
                      disabled={deleteDocument.isPending}
                      className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-400 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                      title="Delete"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-slate-400">No documents uploaded yet.</p>
          )}
        </div>
      ) : null}

      {/* Activity / feedback */}
      {r.feedbacks && r.feedbacks.length > 0 && (
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="mb-4 text-xs font-black uppercase tracking-wide text-slate-400">Activity</h2>
          <div className="space-y-3">
            {r.feedbacks.map((f) => (
              <div key={f.id} className="flex gap-3 rounded-2xl bg-slate-50 px-4 py-3">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-slate-400 shadow-sm">
                  <MessageSquare className="h-3.5 w-3.5" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-slate-700">{formatUsername(f.actorName)}</p>
                  {f.comments && <p className="mt-0.5 text-sm text-slate-600">{f.comments}</p>}
                  {f.actionAt && <p className="mt-1 text-xs text-slate-400">{fmtDateTime(f.actionAt)}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action panel */}
      {dialog ? (
        <div className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
          <h2 className="mb-3 font-black text-slate-800">
            {dialog === "approve" ? "Approve Request" : "Reject Request"}
          </h2>
          <TrainingEnrollmentForm
            action={dialog}
            onConfirm={dialog === "approve" ? handleApprove : handleReject}
            onCancel={() => setDialog(null)}
            loading={busy}
          />
        </div>
      ) : (
        (r.status === "DRAFT" || r.status === "SUBMITTED") && (
          <div className="flex flex-wrap gap-2">
            {r.status === "DRAFT" && (
              <button
                onClick={handleSubmit}
                disabled={busy}
                className="rounded-2xl bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
              >
                Submit for Approval
              </button>
            )}
            {r.status === "SUBMITTED" && (
              <>
                <button
                  onClick={() => setDialog("approve")}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 rounded-2xl bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-50"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Approve
                </button>
                <button
                  onClick={() => setDialog("reject")}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 rounded-2xl bg-red-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm hover:bg-red-700 disabled:opacity-50"
                >
                  <XCircle className="h-4 w-4" />
                  Reject
                </button>
              </>
            )}
            {canCancel && (
              <button
                onClick={handleCancel}
                disabled={busy}
                className="rounded-2xl border border-slate-200 bg-white px-5 py-2.5 text-sm font-bold text-slate-600 hover:bg-slate-50 disabled:opacity-50"
              >
                Cancel Request
              </button>
            )}
          </div>
        )
      )}
    </div>
  );
}
